import { useEffect, useMemo, useState } from 'react'
import {Building2, Users, Briefcase, Star, AlertTriangle, BookOpen, TrendingUp, DollarSign, CheckCircle2, Trophy, Award} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'

interface Evaluation {
  collaboratorUserId?: string
  collaboratorName?: string
  companyUserId?: string
  companyName?: string
  average?: number
}

interface Vacancy {
  companyUserId?: string
  companyName?: string
  industry?: string
  salaryMin?: number
  salaryMax?: number
  salary?: number
  status?: string
  createdAt?: string
}

interface Company {
  _id: string
  userId?: string
  commercialName?: string
  displayName?: string
  industry?: string
}

interface RankItem {
  id: string
  name: string
  value: number
  count: number
}

function formatMoney(n: number): string {
  if (!n || Number.isNaN(n)) return '—'
  return '$' + Math.round(n).toLocaleString('es-MX')
}

function AdminReportes() {
  const [loading, setLoading] = useState(true)
  const [companiesCount, setCompaniesCount] = useState(0)
  const [collaboratorsCount, setCollaboratorsCount] = useState(0)
  const [vacanciesActive, setVacanciesActive] = useState(0)
  const [hires, setHires] = useState(0)
  const [casesTotal, setCasesTotal] = useState(0)
  const [trainingsTotal, setTrainingsTotal] = useState(0)
  const [enrollmentsTotal, setEnrollmentsTotal] = useState(0)
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [vacancies, setVacancies] = useState<Vacancy[]>([])
  const [companies, setCompanies] = useState<Company[]>([])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [
          companiesRes,
          collaboratorsRes,
          vacanciesRes,
          linksRes,
          casesRes,
          trainingsRes,
          enrollmentsRes,
          evaluationsRes,
        ] = await Promise.all([
          lumi.entities.companies.list({ limit: 500 }),
          lumi.entities.collaborators.list({ limit: 500 }),
          lumi.entities.vacancies.list({ limit: 500 }),
          lumi.entities.links.list({ limit: 500 }),
          lumi.entities.cases.list({ limit: 500 }),
          lumi.entities.trainings.list({ limit: 500 }),
          lumi.entities.enrollments.list({ limit: 500 }),
          lumi.entities.evaluations.list({ limit: 500 }),
        ])

        const companiesList = (companiesRes.list ?? []) as Company[]
        const vacanciesList = (vacanciesRes.list ?? []) as Vacancy[]
        const linksList = (linksRes.list ?? []) as { status?: string }[]

        setCompaniesCount(companiesRes.total ?? companiesList.length)
        setCollaboratorsCount(collaboratorsRes.total ?? collaboratorsRes.list?.length ?? 0)
        setVacanciesActive(vacanciesList.filter((v) => v.status === 'publicada').length)
        setHires(linksList.filter((l) => l.status === 'aceptada' || l.status === 'finalizada').length)
        setCasesTotal(casesRes.total ?? casesRes.list?.length ?? 0)
        setTrainingsTotal(trainingsRes.total ?? trainingsRes.list?.length ?? 0)
        setEnrollmentsTotal(enrollmentsRes.total ?? enrollmentsRes.list?.length ?? 0)
        setEvaluations((evaluationsRes.list ?? []) as Evaluation[])
        setVacancies(vacanciesList)
        setCompanies(companiesList)
      } catch (e) {
        console.error('[AdminReportes] error:', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // Salario promedio por sector (giro), a partir de vacantes
  const salaryBySector = useMemo(() => {
    const map = new Map<string, { sum: number; count: number }>()
    vacancies.forEach((v) => {
      const sector = v.industry?.trim()
      if (!sector) return
      const min = Number(v.salaryMin) || 0
      const max = Number(v.salaryMax) || 0
      const single = Number(v.salary) || 0
      const value = max && min ? (min + max) / 2 : single || min || max
      if (!value) return
      const prev = map.get(sector) ?? { sum: 0, count: 0 }
      map.set(sector, { sum: prev.sum + value, count: prev.count + 1 })
    })
    return Array.from(map.entries())
      .map(([sector, { sum, count }]) => ({ sector, avg: sum / count, count }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 6)
  }, [vacancies])

  // Sectores con mayor crecimiento (por número de vacantes publicadas)
  const sectorsByVolume = useMemo(() => {
    const map = new Map<string, number>()
    vacancies.forEach((v) => {
      const sector = v.industry?.trim()
      if (!sector) return
      map.set(sector, (map.get(sector) ?? 0) + 1)
    })
    const max = Math.max(1, ...Array.from(map.values()))
    return Array.from(map.entries())
      .map(([sector, count]) => ({ sector, count, pct: Math.round((count / max) * 100) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6)
  }, [vacancies])

  // Ranking de colaboradores mejor evaluados
  const topCollaborators = useMemo<RankItem[]>(() => {
    const map = new Map<string, { name: string; sum: number; count: number }>()
    evaluations.forEach((ev) => {
      const id = ev.collaboratorUserId
      if (!id || !ev.average) return
      const prev = map.get(id) ?? { name: ev.collaboratorName ?? 'Colaborador', sum: 0, count: 0 }
      map.set(id, { name: ev.collaboratorName ?? prev.name, sum: prev.sum + Number(ev.average), count: prev.count + 1 })
    })
    return Array.from(map.entries())
      .map(([id, { name, sum, count }]) => ({ id, name, value: sum / count, count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
  }, [evaluations])

  // Ranking de empresas mejor evaluadas (promedio de evaluaciones que emiten)
  const topCompanies = useMemo<RankItem[]>(() => {
    const nameById = new Map<string, string>()
    companies.forEach((c) => {
      if (c.userId) nameById.set(c.userId, c.commercialName ?? c.displayName ?? 'Empresa')
    })
    const map = new Map<string, { name: string; sum: number; count: number }>()
    evaluations.forEach((ev) => {
      const id = ev.companyUserId
      if (!id || !ev.average) return
      const name = ev.companyName ?? nameById.get(id) ?? 'Empresa'
      const prev = map.get(id) ?? { name, sum: 0, count: 0 }
      map.set(id, { name, sum: prev.sum + Number(ev.average), count: prev.count + 1 })
    })
    return Array.from(map.entries())
      .map(([id, { name, sum, count }]) => ({ id, name, value: sum / count, count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
  }, [evaluations, companies])

  const globalAvg = useMemo(() => {
    const vals = evaluations.map((e) => Number(e.average) || 0).filter((v) => v > 0)
    if (vals.length === 0) return 0
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
  }, [evaluations])

  const kpis = [
    { icon: Building2, label: 'Empresas registradas', value: companiesCount, color: 'bg-blue-50 text-blue-600' },
    { icon: Users, label: 'Colaboradores registrados', value: collaboratorsCount, color: 'bg-emerald-50 text-emerald-600' },
    { icon: Briefcase, label: 'Vacantes activas', value: vacanciesActive, color: 'bg-amber-50 text-amber-600' },
    { icon: CheckCircle2, label: 'Contrataciones', value: hires, color: 'bg-violet-50 text-violet-600' },
  ]

  const secondaryKpis = [
    { icon: Star, label: 'Evaluación global', value: globalAvg ? `${globalAvg}/10` : '—' },
    { icon: AlertTriangle, label: 'Casos registrados', value: casesTotal },
    { icon: BookOpen, label: 'Capacitaciones', value: trainingsTotal },
    { icon: Award, label: 'Inscripciones', value: enrollmentsTotal },
  ]

  return (
    <AdminLayout title="Dashboard global" subtitle="Métricas y tendencias de toda la plataforma Beeznz Work">
          {loading ? (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}
            </div>
          ) : (
            <>
              {/* KPIs principales */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {kpis.map(({ icon: Icon, label, value, color }) => (
                  <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
                      <Icon size={20} />
                    </div>
                    <p className="mt-4 text-3xl font-bold text-slate-900">{value}</p>
                    <p className="mt-1 text-xs font-medium text-slate-500">{label}</p>
                  </div>
                ))}
              </div>

              {/* KPIs secundarios */}
              <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {secondaryKpis.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                      <Icon size={16} />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900">{value}</p>
                      <p className="text-[11px] font-medium text-slate-500">{label}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-2">
                {/* Salario promedio por sector */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <DollarSign size={18} className="text-emerald-600" />
                    <h2 className="text-base font-semibold text-slate-900">Salario promedio por sector</h2>
                  </div>
                  {salaryBySector.length === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-400">Aún no hay datos salariales suficientes.</p>
                  ) : (
                    <div className="space-y-3">
                      {salaryBySector.map(({ sector, avg, count }) => (
                        <div key={sector} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700">{sector}</p>
                            <p className="text-[11px] text-slate-400">{count} vacante{count !== 1 ? 's' : ''}</p>
                          </div>
                          <p className="text-sm font-bold text-emerald-600">{formatMoney(avg)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sectores con mayor crecimiento */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <TrendingUp size={18} className="text-amber-600" />
                    <h2 className="text-base font-semibold text-slate-900">Sectores con mayor actividad</h2>
                  </div>
                  {sectorsByVolume.length === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-400">Aún no hay vacantes publicadas.</p>
                  ) : (
                    <div className="space-y-3">
                      {sectorsByVolume.map(({ sector, count, pct }) => (
                        <div key={sector}>
                          <div className="mb-1 flex items-center justify-between text-sm">
                            <span className="font-medium text-slate-700">{sector}</span>
                            <span className="text-xs font-semibold text-slate-500">{count}</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Empresas mejor evaluadas */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <Trophy size={18} className="text-blue-600" />
                    <h2 className="text-base font-semibold text-slate-900">Empresas mejor evaluadas</h2>
                  </div>
                  {topCompanies.length === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-400">Aún no hay evaluaciones registradas.</p>
                  ) : (
                    <ol className="space-y-2.5">
                      {topCompanies.map((c, i) => (
                        <li key={c.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
                          <div className="flex items-center gap-3">
                            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-400 text-slate-900' : 'bg-slate-200 text-slate-600'}`}>{i + 1}</span>
                            <div>
                              <p className="text-sm font-medium text-slate-800">{c.name}</p>
                              <p className="text-[11px] text-slate-400">{c.count} evaluación{c.count !== 1 ? 'es' : ''}</p>
                            </div>
                          </div>
                          <span className="flex items-center gap-1 text-sm font-bold text-blue-600">
                            <Star size={13} className="fill-blue-600" />{Math.round(c.value * 10) / 10}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                {/* Colaboradores mejor evaluados */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <Award size={18} className="text-violet-600" />
                    <h2 className="text-base font-semibold text-slate-900">Colaboradores mejor evaluados</h2>
                  </div>
                  {topCollaborators.length === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-400">Aún no hay evaluaciones registradas.</p>
                  ) : (
                    <ol className="space-y-2.5">
                      {topCollaborators.map((c, i) => (
                        <li key={c.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
                          <div className="flex items-center gap-3">
                            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-400 text-slate-900' : 'bg-slate-200 text-slate-600'}`}>{i + 1}</span>
                            <div>
                              <p className="text-sm font-medium text-slate-800">{c.name}</p>
                              <p className="text-[11px] text-slate-400">{c.count} evaluación{c.count !== 1 ? 'es' : ''}</p>
                            </div>
                          </div>
                          <span className="flex items-center gap-1 text-sm font-bold text-violet-600">
                            <Star size={13} className="fill-violet-600" />{Math.round(c.value * 10) / 10}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </>
          )}
    </AdminLayout>
  )
}

export default AdminReportes
